/**
 * Section « Apprentissage ».
 *
 * Portée : les actions `learn` uniquement. Le mode Parcours (`lessons`) écrit
 * dans les mêmes colonnes mais répond à une logique différente — exercices
 * imposés, pas de choix de morceau — et les mélanger fausserait la difficulté
 * moyenne comme la répartition des mains.
 */

import { DifficultyChart } from '../components/DifficultyChart';
import { HandSplitChart } from '../components/HandSplitChart';
import { LearnOptionsChart } from '../components/LearnOptionsChart';
import { SectionShell } from '../components/SectionShell';
import { StatTile } from '../components/StatTile';
import { WeeklyDifficultyChart } from '../components/WeeklyDifficultyChart';
import { useAuth } from '../hooks/useAuth';
import { useLearningStats } from '../hooks/usePeriodStats';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { formatDecimal, formatDuration, formatInteger, formatPercent } from '../lib/format';

export function LearningPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();

  const { data, error, isPending, isFetching } = useLearningStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
  });

  const summary = data?.summary;

  return (
    <SectionShell
      lastSyncAt={data?.meta.lastSyncAt}
      isPending={isPending}
      isFetching={isFetching}
      error={error}
      hasData={Boolean(data)}
    >
      <div className="kpi-row">
        <StatTile
          label="Apprentissages"
          value={formatInteger(summary?.totalLearns ?? 0)}
          hint={`${formatInteger(summary?.uniqueUsers ?? 0)} utilisateurs concernés`}
        />
        <StatTile
          label="Durée moyenne"
          value={formatDuration(summary?.avgDurationSeconds ?? 0)}
          hint={`médiane ${formatDuration(summary?.medianDurationSeconds ?? 0)}`}
        />
        <StatTile
          label="Difficulté moyenne"
          value={`${formatDecimal(summary?.avgDifficulty ?? 0)} / 5`}
          hint="difficulté annoncée par le catalogue"
        />
        <StatTile
          label="Taux d'erreur"
          value={formatPercent(summary?.errorRatePercent ?? 0)}
          hint={`${formatInteger(summary?.totalWrongNotes ?? 0)} fausses notes`}
        />
        <StatTile
          label="Notes jouées"
          value={formatInteger(summary?.totalNotesPressed ?? 0)}
          hint="cumul sur la période"
        />
      </div>

      <div className="chart-grid">
        <HandSplitChart data={data?.hands ?? []} />
        <LearnOptionsChart data={data?.options ?? []} />

        <DifficultyChart data={data?.difficulty ?? []} variant="volume" />
        <DifficultyChart data={data?.difficulty ?? []} variant="errors" />

        <div className="chart-grid__wide">
          <WeeklyDifficultyChart data={data?.weeklyDifficulty ?? []} />
        </div>
      </div>
    </SectionShell>
  );
}
